import React, { useMemo } from 'react';
import { Flame } from './CustomIcons';

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export default function StreakCalendar({ loginDates = [], streakDays = 0, bonusLabel = null }) {
  // Build a 5-week (35-day) grid ending today
  const cells = useMemo(() => {
    const today = new Date();
    const activeDates = new Set(loginDates);
    const result = [];
    for (let i = 34; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      const isToday = i === 0;
      const active = activeDates.has(iso);
      result.push({ iso, dayOfWeek: d.getDay(), active, isToday, label: d.getDate() });
    }
    return result;
  }, [loginDates]);

  // Pad front so first cell aligns to correct day-of-week
  const startPad = cells[0]?.dayOfWeek ?? 0;

  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '18px 20px' }} data-testid="streak-calendar">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Flame size={16} color="#F97316" />
          <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>
            {streakDays > 0 ? `${streakDays}-Day Streak` : 'Streak Calendar'}
          </span>
        </div>
        {bonusLabel && (
          <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: 'rgba(249,115,22,0.15)', color: '#F97316', fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="streak-bonus-label">
            +{bonusLabel}
          </span>
        )}
      </div>

      {/* Day labels */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px', marginBottom: '4px' }}>
        {DAYS.map((d, i) => (
          <div key={`day-${i}`} style={{ textAlign: 'center', fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
        {/* Leading empty cells */}
        {Array.from({ length: startPad }).map((_, i) => (
          <div key={`pad-${i}`} />
        ))}
        {/* Day cells */}
        {cells.map((cell) => (
          <div
            key={cell.iso}
            title={cell.iso}
            style={{
              aspectRatio: '1',
              borderRadius: '5px',
              background: cell.active
                ? (cell.isToday ? '#F97316' : 'rgba(249,115,22,0.45)')
                : (cell.isToday ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)'),
              border: cell.isToday ? '2px solid #F97316' : '1px solid transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '9px', color: cell.active ? '#fff' : 'var(--text-muted)',
              fontWeight: cell.isToday ? 700 : 400,
              transition: 'background 0.2s',
            }}
          />
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '14px', marginTop: '10px', justifyContent: 'flex-end' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: 'rgba(249,115,22,0.45)' }} />
          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Active</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: 'rgba(255,255,255,0.06)' }} />
          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Inactive</span>
        </div>
      </div>
    </div>
  );
}
